// src/components/admin/TierPricingEditor.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Skeleton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
  InputAdornment,
  IconButton,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SaveIcon from "@mui/icons-material/Save";
import EuroIcon from "@mui/icons-material/Euro";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GroupIcon from "@mui/icons-material/Group";
import TokenIcon from "@mui/icons-material/Token";
import DescriptionIcon from "@mui/icons-material/Description";
import MicIcon from "@mui/icons-material/Mic";
import FolderIcon from "@mui/icons-material/Folder";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "../../i18n";
import api from "../../api/api";
import { athenaColors } from "../../theme/palette";
import {
  athenaDarkPaperSx,
  athenaBackdropSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
} from "../layout/AthenaDialogStyles";
import { glass } from "../../theme/glassTokens";

interface AdminTier {
  id: number;
  name: string;
  display_name: string;
  description: string;
  price_monthly_cents: number;
  price_monthly: string;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  stripe_synced: boolean;
  max_patients: number;
  max_documents_per_month: number;
  max_tokens_per_month: number;
  max_voice_minutes_per_month: number;
  max_users: number;
  max_environments: number;
  can_edit_records: boolean;
  can_use_ai_extraction: boolean;
  can_use_voice: boolean;
  can_export_csv: boolean;
  can_export_api: boolean;
  can_use_api: boolean;
  can_cross_org_share: boolean;
  can_bulk_upload: boolean;
  max_bulk_upload_patients: number;
  has_analytics_view: boolean;
  has_analytics_filter: boolean;
  has_sso: boolean;
  has_audit_logs: boolean;
  has_priority_support: boolean;
  requires_institutional_agreement: boolean;
  trial_days: number;
  display_order: number;
}

type EditableTier = Partial<
  Omit<AdminTier, "id" | "name" | "stripe_product_id" | "stripe_price_id" | "stripe_synced" | "price_monthly">
>;

const QUOTA_FIELDS: { key: keyof AdminTier; label: string; icon: React.ReactNode; unit: string; step: string }[] = [
  { key: "max_patients", label: "Max Patients", icon: <GroupIcon sx={{ fontSize: 16 }} />, unit: "", step: "1" },
  { key: "max_documents_per_month", label: "Documents / Month", icon: <DescriptionIcon sx={{ fontSize: 16 }} />, unit: "", step: "1" },
  { key: "max_tokens_per_month", label: "Tokens / Month", icon: <TokenIcon sx={{ fontSize: 16 }} />, unit: "", step: "1000" },
  { key: "max_voice_minutes_per_month", label: "Voice Min / Month", icon: <MicIcon sx={{ fontSize: 16 }} />, unit: "min", step: "1" },
  { key: "max_users", label: "Max Users", icon: <GroupIcon sx={{ fontSize: 16 }} />, unit: "", step: "1" },
  { key: "max_environments", label: "Max Environments", icon: <FolderIcon sx={{ fontSize: 16 }} />, unit: "", step: "1" },
  { key: "max_bulk_upload_patients", label: "Bulk Upload Limit", icon: <UploadFileIcon sx={{ fontSize: 16 }} />, unit: "", step: "1" },
];

const FEATURE_FLAGS: { key: keyof AdminTier; label: string }[] = [
  { key: "can_edit_records", label: "Edit Records" },
  { key: "can_use_ai_extraction", label: "AI Extraction" },
  { key: "can_use_voice", label: "Voice Transcription" },
  { key: "can_export_csv", label: "Export CSV" },
  { key: "can_export_api", label: "Export API" },
  { key: "can_use_api", label: "API Access" },
  { key: "can_cross_org_share", label: "Cross-Org Sharing" },
  { key: "can_bulk_upload", label: "Bulk Upload" },
  { key: "has_analytics_view", label: "Analytics View" },
  { key: "has_analytics_filter", label: "Analytics Filter (NL)" },
  { key: "has_sso", label: "SSO" },
  { key: "has_audit_logs", label: "Audit Logs" },
  { key: "has_priority_support", label: "Priority Support" },
  { key: "requires_institutional_agreement", label: "Requires Agreement" },
];

const TIER_ACCENTS: Record<string, string> = {
  free: "#78909c",
  starter: athenaColors.periwinkle,
  professional: athenaColors.royalBlue,
  team: athenaColors.softPurple,
  enterprise: athenaColors.orange,
};

const formatQuota = (val: number, key: string): string => {
  if (val === 0) {
    if (key === "max_tokens_per_month" || key === "max_documents_per_month" || key === "max_voice_minutes_per_month")
      return "None";
    return "Unlimited";
  }
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
  return String(val);
};

const TierPricingEditor: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();

  const [tiers, setTiers] = useState<AdminTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editTier, setEditTier] = useState<AdminTier | null>(null);
  const [editDraft, setEditDraft] = useState<EditableTier>({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<any[] | null>(null);

  const fetchTiers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/billing/admin/tiers/");
      setTiers(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t("tier_editor.error_load", "Failed to load tiers."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  const openEdit = (tier: AdminTier) => {
    setEditTier(tier);
    setEditDraft({});
    setSuccess(null);
    setError(null);
  };

  const closeEdit = () => {
    setEditTier(null);
    setEditDraft({});
  };

  const setDraftField = <K extends keyof EditableTier>(field: K, value: EditableTier[K]) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  const currentVal = <K extends keyof AdminTier>(field: K): AdminTier[K] => {
    if (field in editDraft) return editDraft[field as keyof EditableTier] as AdminTier[K];
    return editTier![field];
  };

  const hasChanges = useMemo(() => {
    if (!editTier) return false;
    return Object.keys(editDraft).some(
      (k) => editDraft[k as keyof EditableTier] !== editTier[k as keyof AdminTier]
    );
  }, [editTier, editDraft]);

  const handleSave = useCallback(async () => {
    if (!editTier || !hasChanges) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.patch(`/billing/admin/tiers/${editTier.name}/`, editDraft);
      const updated: AdminTier = res.data;
      setTiers((prev) => prev.map((t) => (t.name === updated.name ? updated : t)));
      setEditTier(updated);
      setEditDraft({});
      const stripeNote = res.data.stripe_sync
        ? ` Stripe sync: ${res.data.stripe_sync.status}.`
        : "";
      setSuccess(`${updated.display_name} ${t("tier_editor.updated_successfully", "updated successfully.")}${stripeNote}`);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          JSON.stringify(err.response?.data) ||
          t("tier_editor.error_save", "Failed to save tier.")
      );
    } finally {
      setSaving(false);
    }
  }, [editTier, editDraft, hasChanges]);

  const handleSyncAll = useCallback(async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    setSyncResults(null);
    try {
      const res = await api.post("/billing/admin/tiers/sync-stripe/");
      setTiers(res.data.tiers);
      setSyncResults(res.data.sync_results);
      const synced = res.data.sync_results.filter((r: any) => r.status === "synced").length;
      const errors = res.data.sync_results.filter((r: any) => r.status === "error").length;
      setSuccess(
        `Stripe sync complete: ${synced} synced, ${res.data.sync_results.length - synced - errors} skipped${errors ? `, ${errors} errors` : ""}.`
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || t("tier_editor.error_sync", "Stripe sync failed."));
    } finally {
      setSyncing(false);
    }
  }, []);

  // ── Loading skeleton ──
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2, mb: 2 }} />
        <Grid container spacing={2}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={i}>
              <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error && tiers.length === 0) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={fetchTiers} size="small" sx={{ ml: 2 }}>
          {t("tier_editor.retry", "Retry")}
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${athenaColors.deepNavy}, ${athenaColors.royalBlue})`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <StorefrontIcon sx={{ color: theme.palette.common.white, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
              {t("admin.tiers.title", "Subscription Tiers")}
            </Typography>
            <Typography variant="caption" sx={{ color: textSecondary }}>
              {t(
                "admin.tiers.subtitle",
                "Edit tier pricing, quotas, and feature flags. Price changes auto-sync to Stripe."
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Tooltip title={t("tier_editor.sync_tooltip", "Sync all paid tiers to Stripe — creates/updates Products and Prices")}>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                syncing ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <SyncIcon />
              }
              disabled={syncing}
              onClick={handleSyncAll}
              sx={{
                fontWeight: 600,
                textTransform: "none",
                borderColor: alpha(athenaColors.royalBlue, 0.4),
                color: textPrimary,
                "&:hover": {
                  borderColor: athenaColors.royalBlue,
                  background: alpha(athenaColors.royalBlue, 0.08),
                },
              }}
            >
              {syncing ? t("tier_editor.syncing", "Syncing…") : t("tier_editor.sync_all", "Sync All to Stripe")}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Alerts ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* ── Sync results detail ── */}
      {syncResults && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: glass.panel,
            border: `1px solid ${glass.panelBorder}`,
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 700, mb: 1 }}>
            {t("tier_editor.sync_results_title", "Stripe Sync Results")}
          </Typography>
          {syncResults.map((r: any) => (
            <Box key={r.tier} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              {r.status === "synced" ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
              ) : r.status === "error" ? (
                <ErrorOutlineIcon sx={{ fontSize: 16, color: "error.main" }} />
              ) : (
                <LinkOffIcon sx={{ fontSize: 16, color: textSecondary }} />
              )}
              <Typography variant="body2" sx={{ color: textPrimary }}>
                <strong>{r.tier}</strong> — {r.status}
                {r.reason && ` (${r.reason})`}
                {r.error && `: ${r.error}`}
              </Typography>
            </Box>
          ))}
          <Button size="small" onClick={() => setSyncResults(null)} sx={{ mt: 1, color: textSecondary }}>
            {t("tier_editor.dismiss", "Dismiss")}
          </Button>
        </Box>
      )}

      {/* ── Tier Cards Grid ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 2,
        }}
      >
        {tiers.map((tier) => {
          const accent = TIER_ACCENTS[tier.name] || athenaColors.periwinkle;
          const enabledFlags = FEATURE_FLAGS.filter((f) => tier[f.key] === true);

          return (
            <Box
              key={tier.name}
              sx={{
                p: 2,
                borderRadius: 2,
                background: glass.panel,
                border: `1px solid ${glass.panelBorder}`,
                backdropFilter: "blur(8px)",
                transition: "border-color 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  borderColor: alpha(accent, 0.5),
                  transform: "translateY(-2px)",
                },
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Accent top border */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.3)})`,
                }}
              />

              {/* Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                    {tier.display_name}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: accent }}>
                    {tier.price_monthly_cents === 0
                      ? t("tier_editor.free_price", "Free")
                      : `€${(tier.price_monthly_cents / 100).toFixed(0)}`}
                    {tier.price_monthly_cents > 0 && (
                      <Typography component="span" variant="caption" sx={{ color: textSecondary, ml: 0.5 }}>
                        {t("tier_editor.per_month", "/mo")}
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => openEdit(tier)} sx={{ color: textSecondary }}>
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Stripe status */}
              <Box sx={{ mb: 1.5 }}>
                {tier.stripe_synced ? (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                    label={t("tier_editor.stripe_synced", "Stripe Synced")}
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      height: 22,
                      backgroundColor: alpha(theme.palette.success.light, 0.12),
                      color: theme.palette.success.light,
                      "& .MuiChip-icon": { color: theme.palette.success.light },
                    }}
                  />
                ) : tier.name === "free" || tier.name === "enterprise" ? (
                  <Chip
                    label={tier.name === "free" ? t("tier_editor.no_stripe", "No Stripe") : t("tier_editor.custom", "Custom")}
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      height: 22,
                      backgroundColor: alpha(textSecondary, 0.08),
                      color: textSecondary,
                    }}
                  />
                ) : (
                  <Chip
                    icon={<LinkOffIcon sx={{ fontSize: "14px !important" }} />}
                    label={t("tier_editor.not_synced", "Not Synced")}
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      height: 22,
                      backgroundColor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.main,
                      "& .MuiChip-icon": { color: theme.palette.warning.main },
                    }}
                  />
                )}
              </Box>

              {/* Quotas summary */}
              <Box sx={{ flex: 1 }}>
                {QUOTA_FIELDS.slice(0, 5).map((q) => (
                  <Box
                    key={q.key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.3,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: textSecondary }}>
                      {t(`tier_editor.${q.key}`, q.label)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: textPrimary }}>
                      {formatQuota(tier[q.key] as number, q.key)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Feature flags count */}
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${glass.rowDivider}` }}>
                <Typography variant="caption" sx={{ color: textSecondary }}>
                  {enabledFlags.length} / {FEATURE_FLAGS.length} {t("tier_editor.features_enabled", "features enabled")}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Edit Tier Dialog ── */}
      <Dialog
        open={!!editTier}
        onClose={closeEdit}
        maxWidth="md"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: { ...athenaDarkPaperSx, maxHeight: "85vh" } }}
      >
        {editTier && (
          <>
            <DialogTitle sx={athenaDarkTitleSx}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: alpha(TIER_ACCENTS[editTier.name] || athenaColors.periwinkle, 0.2),
                  border: `1px solid ${alpha(TIER_ACCENTS[editTier.name] || athenaColors.periwinkle, 0.35)}`,
                  flexShrink: 0,
                }}
              >
                <StorefrontIcon sx={{ color: TIER_ACCENTS[editTier.name] || athenaColors.periwinkle, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t("tier_editor.edit_prefix", "Edit")} {editTier.display_name}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
                  {t("tier_editor.edit_subtitle", "Modify pricing, quotas, and features for this tier")}
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ ...athenaDarkContentSx, overflowY: "auto" }} dividers>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              {/* ── Pricing Section ── */}
              <Typography
                variant="overline"
                sx={{
                  color: athenaColors.periwinkle,
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  display: "block",
                  mb: 1.5,
                }}
              >
                {t("tier_editor.section_pricing", "Pricing")}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("tier_editor.label_price_monthly", "Monthly Price (cents)")}
                    fullWidth
                    size="small"
                    type="number"
                    value={currentVal("price_monthly_cents")}
                    onChange={(e) =>
                      setDraftField("price_monthly_cents", parseInt(e.target.value) || 0)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EuroIcon sx={{ fontSize: 16, color: alpha(theme.palette.common.white, 0.5) }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0, step: 100 }}
                    helperText={`€${((currentVal("price_monthly_cents") || 0) / 100).toFixed(2)} ${t("tier_editor.per_month_long", "/ month")}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("tier_editor.label_display_name", "Display Name")}
                    fullWidth
                    size="small"
                    value={currentVal("display_name")}
                    onChange={(e) => setDraftField("display_name", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("tier_editor.label_trial_days", "Trial Days")}
                    fullWidth
                    size="small"
                    type="number"
                    value={currentVal("trial_days")}
                    onChange={(e) => setDraftField("trial_days", parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                    helperText={currentVal("trial_days") > 0 ? `${currentVal("trial_days")}${t("tier_editor.day_free_trial", "-day free trial")}` : t("tier_editor.no_trial", "No trial")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("tier_editor.label_display_order", "Display Order")}
                    fullWidth
                    size="small"
                    type="number"
                    value={currentVal("display_order")}
                    onChange={(e) => setDraftField("display_order", parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("tier_editor.label_description", "Description")}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={currentVal("description")}
                    onChange={(e) => setDraftField("description", e.target.value)}
                  />
                </Grid>
              </Grid>

              {/* Stripe info */}
              {editTier.stripe_product_id && (
                <Box
                  sx={{
                    mb: 3,
                    p: 1.5,
                    borderRadius: 2,
                    background: alpha(athenaColors.periwinkle, 0.08),
                    border: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: athenaColors.periwinkle }} />
                    <Typography variant="caption" sx={{ color: athenaColors.periwinkle, fontWeight: 600 }}>
                      {t("tier_editor.stripe_integration", "Stripe Integration")}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6), display: "block" }}>
                    {t("tier_editor.stripe_product", "Product:")} {editTier.stripe_product_id}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6), display: "block" }}>
                    {t("tier_editor.stripe_price", "Price:")} {editTier.stripe_price_id || "—"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.45), display: "block", mt: 0.5 }}>
                    {t("tier_editor.stripe_price_warning", "Changing the price will archive the old Stripe Price and create a new one automatically.")}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ borderColor: alpha(athenaColors.periwinkle, 0.2), mb: 2.5 }} />

              {/* ── Quotas Section ── */}
              <Typography
                variant="overline"
                sx={{
                  color: athenaColors.periwinkle,
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  display: "block",
                  mb: 1.5,
                }}
              >
                {t("tier_editor.section_quotas", "Quotas & Limits")}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {QUOTA_FIELDS.map((q) => (
                  <Grid item xs={6} sm={4} md={3} key={q.key}>
                    <TextField
                      label={t(`tier_editor.${q.key}`, q.label)}
                      fullWidth
                      size="small"
                      type="number"
                      value={currentVal(q.key)}
                      onChange={(e) =>
                        setDraftField(q.key as keyof EditableTier, parseInt(e.target.value) || 0)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {React.cloneElement(q.icon as React.ReactElement, {
                              sx: { fontSize: 16, color: alpha(theme.palette.common.white, 0.5) },
                            })}
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, step: q.step }}
                      helperText={
                        (currentVal(q.key) as number) === 0
                          ? q.key.includes("token") || q.key.includes("document") || q.key.includes("voice")
                            ? t("tier_editor.quota_none", "None")
                            : t("tier_editor.quota_unlimited", "Unlimited")
                          : undefined
                      }
                    />
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ borderColor: alpha(athenaColors.periwinkle, 0.2), mb: 2.5 }} />

              {/* ── Feature Flags Section ── */}
              <Typography
                variant="overline"
                sx={{
                  color: athenaColors.periwinkle,
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  display: "block",
                  mb: 1,
                }}
              >
                {t("tier_editor.section_features", "Feature Flags")}
              </Typography>

              <Grid container spacing={0.5}>
                {FEATURE_FLAGS.map((f) => (
                  <Grid item xs={6} sm={4} md={3} key={f.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={currentVal(f.key) as boolean}
                          onChange={(e) =>
                            setDraftField(f.key as keyof EditableTier, e.target.checked)
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: athenaColors.periwinkle,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: alpha(athenaColors.periwinkle, 0.5),
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>
                          {t(`tier_editor.${f.key}`, f.label)}
                        </Typography>
                      }
                      sx={{ mr: 0 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </DialogContent>

            <DialogActions sx={athenaDarkActionsSx}>
              <Button onClick={closeEdit} sx={athenaDarkSecondaryButtonSx}>
                {t("action.cancel", "Cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                startIcon={
                  saving ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <SaveIcon />
                }
                sx={athenaDarkPrimaryButtonSx}
              >
                {saving ? t("tier_editor.saving", "Saving…") : t("tier_editor.save_changes", "Save Changes")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TierPricingEditor;
